/**
 * Turns a set of choices into an itemised quote.
 *
 * Every line carries its own confidence label so the UI can distinguish a
 * vendor-published figure from a Teamward planning estimate. This module is
 * public browser code and intentionally contains no commission data or source
 * URLs.
 */

import {
  CONFIDENCE_LABELS,
  FACILITATION_OPTIONS,
  PLANNING_SUPPORT_OPTIONS,
  SERVICE_CHARGE_ALLOWANCE_RATE,
  TAX_RATES,
  foodOptionForId,
  fitsHeadcount,
  parseGroupSize,
  rateFor,
} from './pricingModel';
import { venueEstimateLine } from '../venues/venueCatalog';

export function confidenceLabel(kind, confidence) {
  if (confidence === 'benchmark') {
    return CONFIDENCE_LABELS.benchmark;
  }

  if (confidence === 'allowance') {
    return CONFIDENCE_LABELS.allowance;
  }

  if (confidence === 'teamward') {
    return CONFIDENCE_LABELS.teamward;
  }

  if (confidence === 'unknown') {
    return CONFIDENCE_LABELS.unknown;
  }

  if (confidence === 'confirmed') {
    return CONFIDENCE_LABELS.confirmed;
  }

  switch (kind) {
    case 'activity':
      return "Venue's published rate";

    case 'food':
      return 'Vendor-published catering price';

    case 'serviceCharge':
      return CONFIDENCE_LABELS.allowance;

    case 'tax':
      return 'Official tax rate';

    default:
      return CONFIDENCE_LABELS.published;
  }
}

function unitsFor(rate, headcount) {
  if (rate.basis === 'per_person') {
    return headcount;
  }

  if (rate.basis === 'per_unit') {
    const capacity = rate.unitCapacity || 1;

    return Math.ceil(headcount / capacity);
  }

  return 1;
}

function unitNoun(rate, units) {
  if (rate.basis === 'per_person') {
    return `${units} ${
      units === 1 ? 'person' : 'people'
    }`;
  }

  if (rate.basis === 'per_unit') {
    const label = rate.unitLabel || 'unit';

    return `${units} ${label}${
      units === 1 ? '' : 's'
    }`;
  }

  return 'flat rate';
}

function activityLine(
  vendor,
  { headcount, weekend }
) {
  const rate = rateFor(vendor, {
    weekend,
    headcount,
  });

  if (
    rate.basis === 'quote_only' ||
    rate.low == null
  ) {
    return {
      kind: 'activity',
      vendorId: vendor.id,
      label: vendor.name,
      detail: 'Quoted directly with the vendor',
      basis: 'quote_only',
      low: null,
      high: null,
      confidence: 'unknown',
      confidenceLabel: confidenceLabel(
        'activity',
        'unknown'
      ),
      note: rate.note,
      includesFood: rate.includesFood,
    };
  }

  const units = unitsFor(rate, headcount);

  let low = rate.low * units;
  let high = rate.high * units;

  if (rate.minimum != null) {
    low = Math.max(low, rate.minimum);
    high = Math.max(high, rate.minimum);
  }

  const perUnit =
    rate.low === rate.high
      ? `$${rate.low.toLocaleString()}`
      : `$${rate.low.toLocaleString()}\u2013$${rate.high.toLocaleString()}`;

  const pricingDetail = rate.pricingDetail
    ? `${perUnit} total \u00b7 ${rate.pricingDetail}`
    : `${perUnit} \u00d7 ${unitNoun(
        rate,
        units
      )}${
        weekend &&
        rate.confidence === 'published'
          ? ' (weekend rate)'
          : ''
      }`;

  return {
    kind: 'activity',
    vendorId: vendor.id,
    label: vendor.name,
    detail: pricingDetail,
    basis: rate.basis,
    units,
    low,
    high,
    confidence: rate.confidence,
    confidenceLabel: confidenceLabel(
      'activity',
      rate.confidence
    ),
    capturedOn: rate.capturedOn,
    note: rate.note,
    includesFood: rate.includesFood,
    startingAt: Boolean(rate.startingAt),
  };
}

function foodLine(option, headcount) {
  if (!option) {
    return null;
  }

  const minimumHeadcount =
    option.minHeadcount || 0;
  const billableHeadcount =
    Math.max(
      headcount,
      minimumHeadcount
    );

  let low = option.low;
  let high = option.high;
  let detail =
    `${formatRange(
      option.low,
      option.high,
      true
    )} flat`;

  if (option.basis === 'per_person') {
    low *= billableHeadcount;
    high *= billableHeadcount;
    detail =
      `${formatRange(
        option.low,
        option.high,
        true
      )} × ${billableHeadcount} ` +
      `${
        billableHeadcount === 1
          ? 'person'
          : 'people'
      }`;
  } else if (
    option.basis === 'per_unit'
  ) {
    const capacity =
      option.unitCapacity || 1;
    const unitCount = Math.max(
      1,
      Math.ceil(
        billableHeadcount / capacity
      )
    );
    const unit =
      option.unitLabel || 'unit';

    low *= unitCount;
    high *= unitCount;
    detail =
      `${formatRange(
        option.low,
        option.high,
        true
      )} × ${unitCount} ` +
      `${unit}${
        unitCount === 1 ? '' : 's'
      }` +
      ` (serves up to ${capacity} each)`;
  } else if (
    option.basis === 'per_item'
  ) {
    const itemCount = Math.ceil(
      billableHeadcount *
        (option.itemsPerGuest || 1)
    );
    const unit =
      option.unitLabel || 'item';

    low *= itemCount;
    high *= itemCount;
    detail =
      `${formatRange(
        option.low,
        option.high,
        true
      )} × ${itemCount} ` +
      `${unit}${
        itemCount === 1 ? '' : 's'
      }`;
  } else if (
    option.basis ===
    'per_staff_hour'
  ) {
    const hours = option.hours || 4;
    const staffCount = Math.max(
      1,
      Math.ceil(
        billableHeadcount /
          (option.guestsPerStaff ||
            billableHeadcount ||
            1)
      )
    );

    low *= hours * staffCount;
    high *= hours * staffCount;
    detail =
      `${formatRange(
        option.low,
        option.high,
        true
      )} × ${staffCount} ` +
      `${
        staffCount === 1
          ? 'staff member'
          : 'staff members'
      } × ${hours} hours`;
  }

  if (
    minimumHeadcount > 0 &&
    headcount < minimumHeadcount
  ) {
    detail +=
      ` (${minimumHeadcount}-person minimum)`;
  }

  return {
    kind: 'food',
    optionId: option.id,
    label: option.label,
    detail,
    low,
    high,
    confidence:
      option.confidence || 'benchmark',
    confidenceLabel: confidenceLabel(
      'food',
      option.confidence || 'benchmark'
    ),
    note: option.note,
    sourceName: option.sourceName,
    capturedOn: option.capturedOn,
    startingAt: Boolean(
      option.startingAt
    ),
    surchargeEligible:
      option.surchargeEligible !== false,
  };
}

function facilitationLine(option) {
  if (!option || option.id === 'none') {
    return null;
  }

  if (option.basis === 'quote_only') {
    return {
      kind: 'facilitation',
      label: option.label,
      detail: 'Quoted separately',
      low: null,
      high: null,
      confidence: 'unknown',
      confidenceLabel: confidenceLabel(
        'facilitation',
        'unknown'
      ),
      note: option.note,
    };
  }

  return {
    kind: 'facilitation',
    label: option.label,
    detail:
      `$${option.low.toLocaleString()}` +
      `\u2013` +
      `$${option.high.toLocaleString()} flat`,
    low: option.low,
    high: option.high,
    confidence:
      option.confidence || 'benchmark',
    confidenceLabel: confidenceLabel(
      'facilitation',
      option.confidence || 'benchmark'
    ),
    note: option.note,
  };
}

function planningSupportLine(option) {
  if (!option) {
    return null;
  }

  return {
    kind: 'planningSupport',
    label: option.label,
    detail: option.quoteDetail,
    basis: option.basis,
    low: option.low,
    high: option.high,
    confidence: option.confidence,
    confidenceLabel:
      option.id === 'self'
        ? 'No planning fee'
        : confidenceLabel(
            'planningSupport',
            option.confidence
          ),
    startingAt: Boolean(option.startingAt),
  };
}

function surchargeLines(
  foodSubtotal,
  city,
  includeServiceCharge
) {
  const lines = [];

  if (
    foodSubtotal.low === 0 &&
    foodSubtotal.high === 0
  ) {
    return lines;
  }

  const serviceCharge = includeServiceCharge
    ? {
        low:
          foodSubtotal.low *
          SERVICE_CHARGE_ALLOWANCE_RATE,
        high:
          foodSubtotal.high *
          SERVICE_CHARGE_ALLOWANCE_RATE,
      }
    : {
        low: 0,
        high: 0,
      };

  if (includeServiceCharge) {
    lines.push({
      kind: 'serviceCharge',
      label:
        `Service-charge allowance (` +
        `${Math.round(
          SERVICE_CHARGE_ALLOWANCE_RATE * 100
        )}%)`,
      detail:
        "Optional planning cushion; the provider's actual fee may differ",
      low: serviceCharge.low,
      high: serviceCharge.high,
      confidence: 'allowance',
      confidenceLabel: confidenceLabel(
        'serviceCharge',
        'allowance'
      ),
    });
  }

  const tax = TAX_RATES[city];

  if (tax) {
    const base = {
      low:
        foodSubtotal.low +
        serviceCharge.low,
      high:
        foodSubtotal.high +
        serviceCharge.high,
    };

    lines.push({
      kind: 'tax',
      label: tax.label,
      detail: includeServiceCharge
        ? 'Applied to food, beverage, and the included service-charge allowance'
        : 'Applied to prepared food and beverage',
      low: base.low * tax.foodAndDrink,
      high: base.high * tax.foodAndDrink,
      confidence: 'published',
      confidenceLabel: confidenceLabel(
        'tax',
        'published'
      ),
    });
  }

  return lines;
}

export function buildQuote(input, vendors, venues = []) {
  const {
    headcount = 0,
    city = 'Denver',
    weekend = false,
    venueId = null,
    venueIds = null,
    vendorIds = [],
    foodIds = null,
    foodId = 'none',
    facilitationId = 'none',
    planningSupportId = 'self',
    includeServiceCharge = true,
  } = input || {};

  const selected = vendorIds
    .map((id) =>
      vendors.find(
        (vendor) => vendor.id === id
      )
    )
    .filter(Boolean);

  const selectedVenueIds = [...new Set(Array.isArray(venueIds) ? venueIds : venueId ? [venueId] : [])];
  const selectedVenues = selectedVenueIds.map((id) => venues.find((venue) => venue.id === id)).filter(Boolean);
  const comparingVenues = selectedVenues.length > 1;
  const warnings = comparingVenues ? [
    'Multiple venues selected: Your quote includes more than one venue for comparison. Final pricing will depend on which venue you choose. The estimated total spans the selected venue options; venue costs are not added together.'
  ] : [];

  selectedVenues.forEach((venue) => {
    if (headcount > venue.capacity) {
      warnings.push(`${venue.name} lists capacity up to ${venue.capacity}. At ${headcount} people, choose a larger venue or split the group.`);
    }
  });

  selected.forEach((vendor) => {
    if (!fitsHeadcount(vendor, headcount)) {
      const range = parseGroupSize(
        vendor.groupSize
      );

      warnings.push(
        range && headcount > range.max
          ? `${vendor.name} lists capacity up to ${range.max}. At ${headcount} people this needs splitting into groups or a different venue.`
          : `${vendor.name} lists a minimum of ${
              range ? range.min : '?'
            } people. At ${headcount} the vendor may apply a minimum charge.`
      );
    }
  });

  const activityLines = selected.map(
    (vendor) =>
      activityLine(vendor, {
        headcount,
        weekend,
      })
  );

  const venueLines = selectedVenues.map((venue) => {
    const line = venueEstimateLine(venue, headcount);
    return comparingVenues ? { ...line, detail: `${line.detail} · Venue alternative (choose one)` } : line;
  });

  const selectedFoodIds = Array.from(
    new Set(
      Array.isArray(foodIds)
        ? foodIds.filter(Boolean)
        : foodId &&
            foodId !== 'none'
          ? [foodId]
          : []
    )
  );

  const chosenFoods =
    selectedFoodIds
      .map(foodOptionForId)
      .filter(Boolean);

  const foodBundle = [
    ...venueLines,
    ...activityLines,
  ]
    .filter(Boolean)
    .find(
      (line) => line.includesFood
    );
  const foodIncludedBy = foodBundle
    ? foodBundle.label
    : null;

  if (
    foodIncludedBy &&
    chosenFoods.length > 0
  ) {
    warnings.push(
      `${foodIncludedBy} already includes food in its rate, so separate food is listed but not added to your total. ` +
        'Teamward will confirm whether you still need it.'
    );
  }

  // When a venue or experience already includes food, separate food stays
  // on the plan for reference but is not counted, so the total never pays
  // for the same meal twice.
  const foodLines = chosenFoods
    .map((option) =>
      foodLine(option, headcount)
    )
    .filter(Boolean)
    .map((line) =>
      foodIncludedBy && line.low != null
        ? {
            ...line,
            listedLow: line.low,
            listedHigh: line.high,
            low: 0,
            high: 0,
            surchargeEligible: false,
            includedElsewhere: true,
            detail: `${line.detail} · Not added: ${foodIncludedBy} includes food`,
          }
        : line
    );

  const facilitation =
    facilitationLine(
      FACILITATION_OPTIONS.find(
        (option) =>
          option.id === facilitationId
      )
    );

  const planningSupport =
    planningSupportLine(
      PLANNING_SUPPORT_OPTIONS.find(
        (option) =>
          option.id === planningSupportId
      )
    );

  const surchargeFoodLines =
    foodLines.filter(
      (line) =>
        line.surchargeEligible
    );

  const foodSubtotal = {
    low: surchargeFoodLines.reduce(
      (sum, line) =>
        sum + line.low,
      0
    ),
    high: surchargeFoodLines.reduce(
      (sum, line) =>
        sum + line.high,
      0
    ),
  };

  const lines = [
    ...venueLines,
    ...activityLines,
    ...foodLines,
    ...surchargeLines(
      foodSubtotal,
      city,
      includeServiceCharge
    ),
    ...(facilitation
      ? [facilitation]
      : []),
    ...(planningSupport
      ? [planningSupport]
      : []),
  ];

  const priced = lines.filter(
    (line) => line.low != null && line.kind !== 'venue'
  );

  const pricedVenueLines = venueLines.filter((line) => Number.isFinite(line.low) && Number.isFinite(line.high));
  const venueLow = pricedVenueLines.length ? Math.min(...pricedVenueLines.map((line) => line.low)) : 0;
  const venueHigh = pricedVenueLines.length ? Math.max(...pricedVenueLines.map((line) => line.high)) : 0;
  const totals = {
    low: Math.round(
      priced.reduce(
        (sum, line) => sum + line.low,
        venueLow
      )
    ),
    high: Math.round(
      priced.reduce(
        (sum, line) => sum + line.high,
        venueHigh
      )
    ),
  };

  totals.perPersonLow =
    headcount > 0
      ? totals.low / headcount
      : 0;

  totals.perPersonHigh =
    headcount > 0
      ? totals.high / headcount
      : 0;

  const quotedSeparately = lines
    .filter((line) => line.low == null)
    .map((line) => line.label);

  const confidences = new Set(
    lines
      .filter(
        (line) => line.low != null
      )
      .map(
        (line) => line.confidence
      )
  );

  return {
    input: {
      headcount,
      city,
      weekend,
      venueId: selectedVenueIds[0] || null,
      venueIds: selectedVenueIds,
      vendorIds,
      foodIds: selectedFoodIds,
      foodId:
        selectedFoodIds[0] ||
        'none',
      facilitationId,
      planningSupportId,
      includeServiceCharge,
    },

    lines,
    totals,
    warnings,
    quotedSeparately,
    foodIncludedBy:
      chosenFoods.length > 0
        ? foodIncludedBy
        : null,
    foodBundledBy: foodIncludedBy,

    allRatesSourced:
      confidences.size > 0 &&
      !confidences.has('benchmark') &&
      !confidences.has('allowance'),

    hasEstimates:
      confidences.has('benchmark') ||
      confidences.has('allowance'),

    hasStartingPrices: lines.some(
      (line) => line.startingAt
    ),
  };
}

export function isActiveListing(vendor) {
  return Boolean(
    vendor &&
      vendor.active !== false
  );
}

export function eligibleVendors(
  vendors,
  { city, headcount }
) {
  return vendors
    .filter(isActiveListing)
    .filter((vendor) =>
      city && city !== 'All Cities'
        ? vendor.city === city
        : true
    )
    .filter((vendor) =>
      headcount > 0
        ? fitsHeadcount(
            vendor,
            headcount
          )
        : true
    );
}

export function formatMoney(
  value,
  preserveCents = false
) {
  if (value == null) {
    return '\u2014';
  }

  const roundedValue = preserveCents
    ? Math.round(value * 100) / 100
    : Math.round(value);
  const hasCents =
    preserveCents &&
    !Number.isInteger(roundedValue);

  return `$${roundedValue.toLocaleString(
    'en-US',
    {
      minimumFractionDigits: hasCents
        ? 2
        : 0,
      maximumFractionDigits: hasCents
        ? 2
        : 0,
    }
  )}`;
}

export function formatRange(
  low,
  high,
  preserveCents = false
) {
  if (
    low == null ||
    high == null
  ) {
    return 'Quoted separately';
  }

  const comparableLow = preserveCents
    ? Math.round(low * 100)
    : Math.round(low);
  const comparableHigh = preserveCents
    ? Math.round(high * 100)
    : Math.round(high);

  if (comparableLow === comparableHigh) {
    return formatMoney(
      low,
      preserveCents
    );
  }

  return (
    `${formatMoney(low, preserveCents)}` +
    `\u2013` +
    `${formatMoney(high, preserveCents)}`
  );
}
